// src/feature-module/hotel/hotel-details/hotelDetails.tsx
import React, { useState, useEffect, useRef } from "react";
import Slider, { Settings } from "react-slick";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import RoomDetailModal from "../../../core/common/modal/roomDetailModal";
import { all_routes } from "../../router/all_routes";
import API_URLS from "../../../core/common/apiUrls";
import HotelStickyContent from "./HotelStickyContent";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./hotelDetails.css";

const HotelDetails: React.FC = () => {
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();

  const [hotelDetails, setHotelDetails] = useState<any>(null);
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<any | null>(null);
  const [prebookData, setPrebookData] = useState<any | null>(null);
  const [sidebarInfo, setSidebarInfo] = useState<{
    price: number | null;
    currency: string;
    address: string;
    breakdown?: any[];
  }>({
    price: null,
    currency: "INR",
    address: "",
    breakdown: [],
  });

  // Modal state (React-driven)
  const [roomModalVisible, setRoomModalVisible] = useState(false);
  const [modalRoomData, setModalRoomData] = useState<any | null>(null);
  const [modalRoomImages, setModalRoomImages] = useState<string[]>([]);

  // normalize and compute totals from incoming paxRooms
  const incomingPaxRooms: any[] =
    (location.state?.paxRooms as any[]) ||
    (location.state?.PaxRooms as any[]) ||
    [{ Adults: 2, Children: 0, ChildrenAges: [] }];

  const paxRooms = incomingPaxRooms.map((r: any) => ({
    Adults: Number(r?.Adults || 0),
    Children: Number(r?.Children || 0),
    ChildrenAges: Array.isArray(r?.ChildrenAges) ? r.ChildrenAges : [],
  }));

  const totals = paxRooms.reduce(
    (acc, r) => {
      acc.adults += Number(r.Adults || 0);
      acc.children += Number(r.Children || 0);
      acc.rooms += 1;
      return acc;
    },
    { adults: 0, children: 0, rooms: 0 }
  );

  useEffect(() => {
    const fetchRooms = async () => {
      setLoading(true);
      try {
        const res = await fetch(API_URLS.HOTELS.HOTEL_ROOMS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            HotelCode: location.state?.hotelCode || "1025726",
            CheckIn: location.state?.checkIn || "2025-10-25",
            CheckOut: location.state?.checkOut || "2025-10-27",
            GuestNationality:
              location.state?.GuestNationality ||
              location.state?.guestNationality ||
              "IN",
            PaxRooms: paxRooms,
          }),
        });
        const data = await res.json();
        if (data?.Rooms) {
          setRooms(data.Rooms);
          setHotelDetails(data.Details || {});
          const minRoom = data.Rooms.reduce((prev: any, curr: any) =>
            (prev?.TotalFare || Number.MAX_SAFE_INTEGER) <
            (curr?.TotalFare || Number.MAX_SAFE_INTEGER)
              ? prev
              : curr
          );
          setSidebarInfo({
            price: minRoom?.TotalFare || null,
            currency: data.Currency || "INR",
            address: data.Details?.Address || "Hotel Address",
            breakdown: minRoom?.PriceBreakUp || [],
          });
        } else {
          setError(data?.error || "No rooms available.");
        }
      } catch (err) {
        console.error("fetchRooms error:", err);
        setError("Failed to fetch rooms.");
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Prebook when user selects a room
  const handleSelectRoom = async (room: any) => {
    setSelectedRoom(room);
    setPrebookData(null);
    setSidebarInfo((s) => ({
      ...s,
      breakdown: [],
      price: room?.TotalFare || s.price,
    }));

    try {
      const res = await fetch(API_URLS.HOTELS.PREBOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          BookingCode: room.BookingCode,
          PaxRooms: paxRooms, // forward full paxRooms
        }),
      });
      const data = await res.json();
      if (data) {
        setPrebookData(data);
        const hotelResult = data.HotelResult?.[0] || data?.HotelResult || {};
        const firstRoom = hotelResult?.Rooms?.[0] || hotelResult;
        setSidebarInfo((s) => ({
          price: firstRoom?.TotalFare || room.TotalFare || s.price,
          currency: hotelResult?.Currency || s.currency,
          address: hotelDetails?.Address || s.address,
          breakdown: firstRoom?.PriceBreakUp || s.breakdown,
        }));
      }
    } catch (err) {
      console.error("prebook error:", err);
      setError("Failed to prebook this room.");
    }
  };

  // Open modal (React) with given room data
  const openRoomModal = (room: any, roomImages: string[]) => {
    setModalRoomData(room);
    setModalRoomImages(roomImages || []);
    setRoomModalVisible(true);
  };
  const closeRoomModal = () => {
    setRoomModalVisible(false);
    setModalRoomData(null);
    setModalRoomImages([]);
  };

  // helper: show a small short hint if user hasn't selected
  const renderSelectHint = () => {
    if (selectedRoom) return null;
    return (
      <div className="select-hint d-flex align-items-center mb-3" role="status" aria-live="polite">
        <div className="hint-card p-3 d-flex align-items-center w-100">
          <div className="me-3 hint-dot" aria-hidden />
          <div>
            <strong>Select a room</strong>
            <div className="small text-muted">Choose a room card to see price breakdown and enable booking</div>
          </div>
          <div className="ms-auto d-none d-md-block">
            <i className="fa fa-hand-pointer fa-2x bounce-arrow" aria-hidden></i>
          </div>
        </div>
      </div>
    );
  };

  const breadcrumbs = [
    { label: "Hotel Details", active: false, link: routes.home1 },
    { label: "Hotel", active: false },
    { label: "Hotel Details", active: true },
  ];

  const sliderForRef = useRef<any>(null);
  const sliderForSettings: Settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    arrows: true,
    fade: true,
    adaptiveHeight: true,

    // make header slider autoplay & show dots
    autoplay: true,
    autoplaySpeed: 3500,
    pauseOnHover: true,
    dots: true,
    responsive: [
      { breakpoint: 768, settings: { arrows: false, dots: true } }
    ]
  };

  // settings for room image slider (used inside each room card)
  const roomSliderSettings: Settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    arrows: true,
    dots: true,
    adaptiveHeight: true,

    // autoplay
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    swipeToSlide: true,
    accessibility: true,

    responsive: [
      { breakpoint: 992, settings: { arrows: false, dots: true } },
      { breakpoint: 576, settings: { arrows: false, dots: true } },
    ],
  };

  const parseDescription = () => {
    if (!hotelDetails?.Description) return {};
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      DOMPurify.sanitize(hotelDetails.Description),
      "text/html"
    );

    const sections: Record<string, string> = {};
    let currentTitle = "";
    let buffer: string[] = [];

    doc.body.childNodes.forEach((node) => {
      if (node.nodeName === "P" || node.nodeName === "UL") {
        const text = (node as HTMLElement).outerHTML;
        if ((node as HTMLElement).querySelector("strong")) {
          if (currentTitle && buffer.length) {
            sections[currentTitle] = buffer.join("");
          }
          currentTitle = (node as HTMLElement)
            .querySelector("strong")!
            .textContent!.trim();
          buffer = [text];
        } else {
          buffer.push(text);
        }
      }
    });

    if (currentTitle && buffer.length) {
      sections[currentTitle] = buffer.join("");
    }
    return sections;
  };

  const descriptionSections = parseDescription();

  return (
    <>
      <Breadcrumb
        title="Hotel Details"
        breadcrumbs={breadcrumbs}
        backgroundClass="breadcrumb-bar flightList-banner-bg text-center"
      />

      <div className="content">
        <div className="container">
          <div className="row">
            {/* Main */}
            <div className="col-xl-8">
              {hotelDetails && (
                <>
                  {/* Header Slider — added class hotel-main-slider */}
                  <div className="mb-4 hotel-main-slider">
                    <Slider {...sliderForSettings} ref={sliderForRef}>
                      {(hotelDetails.Images || []).map((img: string, idx: number) => (
                        <div key={idx}>
                          <img
                            src={img}
                            className="img-fluid rounded"
                            alt={`hotel-${idx}`}
                            style={{ maxHeight: "400px", objectFit: "cover", width: "100%" }}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>

                  {/* Header */}
                  <h3>{hotelDetails.HotelName}</h3>
                  <p className="text-muted mb-2">
                    ⭐ {hotelDetails.HotelRating} Star | {hotelDetails.Address},{" "}
                    {hotelDetails.CityName}, {hotelDetails.CountryName}
                  </p>

                  <div className="d-flex flex-wrap gap-3 small mb-3">
                    <span>📍 <a href={`https://www.google.com/maps?q=${hotelDetails.Map}`} target="_blank" rel="noreferrer">View on Map</a></span>
                    <span>🌐 <a href={hotelDetails.HotelWebsiteUrl} target="_blank" rel="noreferrer">Website</a></span>
                    <span>📞 {hotelDetails.PhoneNumber}</span>
                    <span>📧 {hotelDetails.Email}</span>
                  </div>

                  {/* Tabs */}
                  <ul className="nav nav-tabs mb-3" role="tablist">
                    {Object.keys(descriptionSections).map((section, idx) => (
                      <li className="nav-item" key={idx}>
                        <button className={`nav-link ${idx === 0 ? "active" : ""}`} data-bs-toggle="tab" data-bs-target={`#tab-${idx}`}>
                          {section}
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div className="tab-content mb-4">
                    {Object.entries(descriptionSections).map(([title, html], idx) => (
                      <div className={`tab-pane fade ${idx === 0 ? "show active" : ""}`} id={`tab-${idx}`} key={idx}>
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />

                        {title.includes("Overview") && (
                          <p className="mt-3">
                            <b>Check-in:</b> {hotelDetails.CheckInTime} <br />
                            <b>Check-out:</b> {hotelDetails.CheckOutTime}
                          </p>
                        )}

                        {title.includes("Policies") && (
                          <small className="text-muted d-block mt-2">
                            Amenities are subject to availability and may be chargeable as per hotel policy.
                          </small>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Rooms */}
              <div className="rooms-header d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0 fs-18">Rooms & Availability</h5>
                <div className="small text-muted">
                  {totals.rooms} room{totals.rooms > 1 ? "s" : ""} • {totals.adults} adults{totals.children ? ` • ${totals.children} children` : ""}
                </div>
              </div>

              {renderSelectHint()}

              <div className="row">
                {loading && <p>Loading rooms...</p>}
                {error && <p className="text-danger">{error}</p>}

                {!loading &&
                  rooms.map((room, index) => {
                    // normalize room images: prefer room.Images, fallback to hotel images
                    const roomImages: string[] =
                      (room.Images && room.Images.length)
                        ? room.Images
                        : (hotelDetails?.Images || []).slice(0, 6).map((u: string) => u);

                    const isSelected = selectedRoom && selectedRoom.BookingCode === room.BookingCode;

                    // flatten supplements structure if nested
                    const supplementsFlat = (room.Supplements || []).flat?.() || [];

                    return (
                      <div className="col-12 mb-4" key={room.BookingCode ?? index}>
                        <div className={`card h-100 shadow-sm room-card ${isSelected ? "selected" : ""}`} aria-pressed={isSelected}>
                          <div className="room-image">
                            {roomImages && roomImages.length ? (
                              <Slider {...roomSliderSettings} className="room-image-slider" key={room.BookingCode || `room-${index}`}>
                                {roomImages.map((img: string, i: number) => (
                                  <div key={i} className="room-slide">
                                    <img src={img} alt={`room-${index}-img-${i}`} style={{ width: '100%', height: 200, objectFit: 'cover' }} loading="lazy" />
                                  </div>
                                ))}
                              </Slider>
                            ) : (
                              <div className="placeholder-thumb" style={{ height: "200px" }}>No image</div>
                            )}
                          </div>

                          <div className="room-card-content">
                            <div>
                              <h5 className="mb-1">{Array.isArray(room.Name) ? room.Name.join(", ") : room.Name}</h5>
                              <div className="d-flex flex-wrap mb-2 small">
                                <span className="badge bg-info me-2">{room.MealType}</span>
                                {room.Inclusion && <span className="badge bg-light me-2">{room.Inclusion}</span>}

                                {/* Room promotions (already present) */}
                                {room.RoomPromotion?.map((promo: string, i: number) => (
                                  <span key={i} className="badge bg-warning text-dark me-2">{promo}</span>
                                ))}

                                {room.IsRefundable ? <span className="badge bg-success">Refundable</span> : <span className="badge bg-danger">Non-Refundable</span>}
                              </div>

                              {room.CancelPolicies?.length > 0 && (
                                <div className="mb-2 small text-muted">
                                  {room.CancelPolicies.slice(0, 1).map((p: any, i: number) => (
                                    <div key={i}>
                                      <strong>Cancellation:</strong> {p.ChargeType === "Percentage" ? `${p.CancellationCharge}%` : `${p.CancellationCharge}`} ({p.FromDate})
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Supplements display */}
                              {supplementsFlat.length > 0 && (
                                <div className="mb-2 small text-muted">
                                  <strong>Supplements:</strong>
                                  <ul className="mb-0">
                                    {supplementsFlat.map((s: any, si: number) => (
                                      <li key={si}>
                                        {s.Description || s.Description} • {s.Price}{s.Currency ? ` ${s.Currency}` : ''} {s.Type ? `(${s.Type})` : ''}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                            </div>

                            <div className="room-card-footer">
                              <div>
                                <div className="text-primary fw-bold room-price">
                                  {room.TotalFare?.toLocaleString()} {sidebarInfo.currency}
                                </div>
                                <small className="text-muted">Total price</small>
                              </div>

                              <div className="d-flex flex-column align-items-end">
                                <div className="btn-group">
                                  <button
                                    className={`btn btn-sm ${isSelected ? "btn-outline-secondary" : "btn-outline-primary"}`}
                                    onClick={() => handleSelectRoom(room)}
                                    aria-pressed={isSelected}
                                    aria-label={isSelected ? "Selected" : "Select room"}
                                  >
                                    {isSelected ? "Selected" : "Select"}
                                  </button>

                                  <button
                                    className="btn btn-sm btn-light ms-2"
                                    onClick={() => openRoomModal(room, roomImages)}
                                    aria-label="View details"
                                  >
                                    View
                                  </button>
                                </div>

                                {!isSelected && <small className="text-muted mt-1">Tap select to book</small>}
                                {isSelected && <small className="text-success mt-1">Room selected</small>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-xl-4">
              <div className="hotel-sidebar">
                <HotelStickyContent
                  hotelName={hotelDetails?.HotelName}
                  address={hotelDetails?.Address || hotelDetails?.HotelAddress}
                  mapUrl={`https://www.google.com/maps?q=${hotelDetails?.Map}`}
                  checkIn={location.state?.checkIn}
                  checkOut={location.state?.checkOut}
                  adults={totals.adults}
                  children={totals.children}
                  rooms={totals.rooms}
                  price={sidebarInfo.price}
                  currency={sidebarInfo.currency}
                  breakdown={sidebarInfo.breakdown}
                  prebookData={prebookData}
                  paxRooms={paxRooms}
                  selectedRoom={selectedRoom}
                  onBookNow={() =>
                    navigate(all_routes.hotelGuestDetails, {
                      state: {
                        hotelDetails,
                        prebookData,
                        checkIn: location.state?.checkIn,
                        checkOut: location.state?.checkOut,
                        paxRooms: paxRooms,
                        totalAdults: totals.adults,
                        totalChildren: totals.children,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* React-driven modal */}
      <RoomDetailModal
        visible={roomModalVisible}
        onClose={closeRoomModal}
        room={modalRoomData}
        roomImages={modalRoomImages}
        hotelDetails={hotelDetails}
      />
    </>
  );
};

export default HotelDetails;
